import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RequestState, TicketStatus } from '@kfolks/types'
import { isAdmin } from '@/auth'
import { Badge } from '@/shared/components/atoms/ui/badge'
import { Button } from '@/shared/components/atoms/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/atoms/ui/card'
import { useAuth } from '@/hooks/useAuth'
import { type TicketDTO, requestCenterApi } from '@/features/request-center'
import { ticketsApi } from '@/features/tickets'

/**
 * `<TicketsSection>` — Spec 3 Group 16.1 / 16.2.
 *
 * Lists every ticket linked to a request. Columns:
 *   - Title
 *   - Status badge (open / closed)
 *   - "View in Productive" external link (when `externalTicketUrl` set)
 *   - Last-synced timestamp (from `lastSyncedAt`)
 *
 * Admin viewers also see a "Sync now" button that fires
 * `POST /tickets/sync` so they can manually re-run the poll without
 * waiting 6 hours — useful when testing TICKET_STATUS_CHANGED /
 * TICKET_CLOSED end-to-end (Spec 3 smoke #22.3 + #22.4).
 *
 * Silent on 404 — the backend hides the request from unrelated viewers
 * the same way `RequestDetailPage` does, so a 404 from this endpoint
 * just means "nothing to show here". Empty list renders the standard
 * empty-state copy.
 */

export interface TicketsSectionProps {
	requestId: string
	/**
	 * Parent request's current state (RequestDetailPage already has this
	 * from `useQuery(['request', id])`). When supplied AND the ticket is
	 * `closed`, the row's badge surfaces *why* — `closed · cancelled`,
	 * `closed · rejected`, `closed · failed` — instead of the bare
	 * "closed" text. The ticket model only carries open/closed, so the
	 * full lifecycle context lives on the request and we plumb it in.
	 */
	requestState?: RequestState
}

export function TicketsSection({ requestId, requestState }: TicketsSectionProps) {
	const queryClient = useQueryClient()
	const { user } = useAuth()
	const viewerIsAdmin = isAdmin(user?.role)

	const { data, isLoading, isError } = useQuery({
		queryKey: ['tickets-by-request', requestId],
		queryFn: () => requestCenterApi.listTicketsByRequest(requestId),
	})
	const tickets = data?.data ?? []

	// Track the latest manual-sync job so we can show its status inline.
	// `null` = no sync triggered this session; otherwise we poll the job
	// id every 2s until it resolves to completed/failed.
	const [pendingJobId, setPendingJobId] = useState<string | null>(null)
	const triggerMutation = useMutation({
		mutationFn: () => ticketsApi.trigger(),
		onSuccess: (job) => {
			setPendingJobId(job.jobId)
		},
	})
	const { data: jobStatus } = useQuery({
		queryKey: ['ticket-sync-job', pendingJobId],
		queryFn: () => ticketsApi.getStatus(pendingJobId ?? ''),
		enabled: !!pendingJobId,
		// Poll every 2s while accepted/running; stop once it resolves.
		refetchInterval: (query) => {
			const last = query.state.data
			return last && (last.status === 'completed' || last.status === 'failed') ? false : 2000
		},
	})
	// On terminal status, refresh the tickets list (open → closed flips
	// land here), the audit timeline (the sync emits status_changed /
	// closed events the timeline reads from /events), AND the parent
	// request — because `request-center-worker` may have flipped
	// `approved → completed` (all tickets closed) or `approved → failed`
	// (TICKET_REQUEST_FAILED) by the time this resolves.
	if (jobStatus?.status === 'completed' || jobStatus?.status === 'failed') {
		if (pendingJobId === jobStatus.jobId) {
			void queryClient.invalidateQueries({ queryKey: ['tickets-by-request', requestId] })
			void queryClient.invalidateQueries({ queryKey: ['request-audit', requestId] })
			void queryClient.invalidateQueries({ queryKey: ['request', requestId] })
			// Idempotent — `setPendingJobId(null)` would re-render forever; instead clear when the
			// user clicks "Sync now" again or navigates away.
		}
	}

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between gap-3">
					<CardTitle className="text-base">Tickets</CardTitle>
					{viewerIsAdmin ? (
						<div className="flex items-center gap-2 text-xs">
							{pendingJobId ? <SyncStatusBadge status={jobStatus?.status} error={jobStatus?.error ?? null} /> : null}
							<Button
								type="button"
								variant="outline"
								size="sm"
								disabled={triggerMutation.isPending}
								onClick={() => triggerMutation.mutate()}
								data-testid="ticket-sync-trigger"
							>
								{triggerMutation.isPending ? 'Queueing…' : 'Sync now'}
							</Button>
						</div>
					) : null}
				</div>
			</CardHeader>
			<CardContent>
				{isLoading ? (
					<p className="text-sm text-muted-foreground">Loading…</p>
				) : isError ? (
					<p className="text-sm text-muted-foreground">No tickets linked to this request.</p>
				) : tickets.length === 0 ? (
					<p className="text-sm text-muted-foreground">No tickets linked to this request.</p>
				) : (
					<ol className="space-y-3" data-testid="tickets-list">
						{tickets.map((ticket) => (
							<TicketRow key={ticket.ticketId} ticket={ticket} requestState={requestState} />
						))}
					</ol>
				)}
			</CardContent>
		</Card>
	)
}

function SyncStatusBadge({ status, error }: { status?: string; error?: string | null }) {
	if (!status) return <span className="text-muted-foreground">Polling…</span>
	if (status === 'failed') {
		return (
			<span title={error ?? undefined} className="text-destructive">
				Sync failed
			</span>
		)
	}
	if (status === 'completed') return <span className="text-green-600">Sync complete</span>
	if (status === 'running') return <span className="text-muted-foreground">Sync running…</span>
	return <span className="text-muted-foreground">Sync queued</span>
}

function TicketRow({ ticket, requestState }: { ticket: TicketDTO; requestState?: RequestState }) {
	const isClosed = ticket.status === TicketStatus.CLOSED
	const lastSynced = ticket.lastSyncedAt ? new Date(ticket.lastSyncedAt) : null
	const lastSyncedStr = lastSynced && !Number.isNaN(lastSynced.getTime()) ? lastSynced.toLocaleString() : 'never synced'
	const closureContext = isClosed ? requestStateToClosureLabel(requestState) : null

	return (
		<li className="border-l-2 border-muted pl-3" data-testid="ticket-row" data-ticket-id={ticket.ticketId}>
			<div className="flex flex-wrap items-center gap-2 text-sm">
				<span className="font-medium" data-testid="ticket-title">
					{ticket.title}
				</span>
				<Badge variant={isClosed ? 'outline' : 'secondary'} data-testid="ticket-status">
					{closureContext ? `${ticket.status} · ${closureContext}` : ticket.status}
				</Badge>
				{ticket.externalTicketUrl ? (
					<a
						href={ticket.externalTicketUrl}
						target="_blank"
						rel="noopener noreferrer"
						className="text-xs text-primary underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
						data-testid="ticket-external-link"
					>
						View in Productive ↗
					</a>
				) : null}
			</div>
			<p className="text-xs text-muted-foreground">Last synced {lastSyncedStr}</p>
		</li>
	)
}

/**
 * Map a request's terminal state to the closure-context label appended
 * to a closed ticket's badge. Non-terminal states return null — those
 * rows render the bare "closed" because the ticket closed without a
 * matching request lifecycle event (e.g. a Productive operator closed
 * the task while the request is still mid-approval).
 */
function requestStateToClosureLabel(state: RequestState | undefined): string | null {
	switch (state) {
		case RequestState.COMPLETED:
			return 'completed'
		case RequestState.CANCELLED:
			return 'cancelled'
		case RequestState.REJECTED:
			return 'rejected'
		case RequestState.FAILED:
			return 'failed'
		default:
			return null
	}
}
